import {
  addItemToCollection,
  getItemsFromCollection,
  updateItemInCollection,
  addItemsToCollection,
  deleteItemsFromCollection,
  fetchEmployees,
} from "directus/src/operations";

/**
 * Fetch users that report to the current user and are active
 */
export const fetchEmployeesForPlanning = async () => {
  try {
    const query = {
      filter: {
        _or: [
          { reports_to: { user: { _eq: "$CURRENT_USER" } } },
          { user: { _eq: "$CURRENT_USER" } },
        ],
        user: {
          status: { _eq: "active" },
        },
      },
      sort: ["first_name"],
      fields: [
        "id",
        "first_name",
        "last_name",
        "avatar",
        "user.role.name",
        "title",
        "department.name",
      ],
    };
    const response = await fetchEmployees(query);

    return response;
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

/**
 * Fetch timesheet data for a specific user and date range
 */
export const fetchTimesheet = async (userId, startDate, endDate) => {
  try {
    const query = {
      filter: {
        employee: { _eq: userId },
        date: { _between: [startDate, endDate] },
        planned_hours: { _neq: 0 },
      },
      fields: [
        "id",
        "employee.id",
        "employee.first_name",
        "employee.last_name",
        "planned_hours",
        "date",
        "project.id",
        "project.name",
        "job.id",
        "job.name",
        "job.start_date",
        "job.end_date",
        "milestone.id",
        "milestone.name",
        "milestone.start_date",
        "milestone.end_date",
      ],
    };
    const response = await getItemsFromCollection("timesheet", query);

    return response;
  } catch (error) {
    console.error("Error fetching timesheet:", error);
    throw error;
  }
};

/**
 * Check if a timesheet entry exists for the given parameters
 */
export const findExistingTimesheetEntry = async (
  employeeId,
  projectId,
  jobId,
  milestoneId,
  date,
) => {
  try {
    const query = {
      filter: {
        employee: { _eq: employeeId },
        project: { _eq: projectId },
        job: { _eq: jobId },
        milestone: { _eq: milestoneId || null },
        date: { _eq: date },
      },
    };
    const response = await getItemsFromCollection("timesheet", query);

    return response;
  } catch (error) {
    console.error("Error finding existing timesheet entry:", error);
    throw error;
  }
};

/**
 * Update an existing timesheet entry
 */
export const updateTimesheetEntry = async (entryId, plannedHours) => {
  try {
    const response = await updateItemInCollection("timesheet", entryId, {
      planned_hours: plannedHours,
    });
    return response;
  } catch (error) {
    console.error("Error updating timesheet entry:", error);
    throw error;
  }
};

/**
 * Create a new timesheet entry
 */
export const createTimesheetEntry = async (
  employeeId,
  projectId,
  jobId,
  milestoneId,
  date,
  plannedHours,
) => {
  try {
    const query = {
      employee: employeeId,
      project: projectId,
      job: jobId,
      milestone: milestoneId || null,
      date: date,
      planned_hours: plannedHours,
    };
    const response = await addItemToCollection("timesheet", query);
    return response;
  } catch (error) {
    console.error("Error creating timesheet entry:", error);
    throw error;
  }
};

/**
 * Delete timesheet entries for a specific plannable (project/job/milestone combination) - Bulk operation
 */
export const deleteTimesheetEntriesForPlannable = async (
  employeeId,
  projectId,
  jobId,
  milestoneId,
  startDate,
  endDate,
) => {
  try {
    const query = {
      filter: {
        employee: { _eq: employeeId },
        project: { _eq: projectId },
        job: { _eq: jobId },
        milestone: { _eq: milestoneId || null },
        date: { _between: [startDate, endDate] },
        planned_hours: { _nnull: true },
      },
    };

    const existingEntries = await getItemsFromCollection("timesheet", query);

    if (existingEntries.length > 0) {
      const entryIds = existingEntries.map((entry) => entry.id);
      await deleteItemsFromCollection("timesheet", entryIds);
    }

    return { deletedCount: existingEntries.length };
  } catch (error) {
    console.error("Error deleting timesheet entries for plannable:", error);
    throw error;
  }
};

export const bulkDeleteTimesheetEntries = async (
  employeeId,
  plannables,
  startDate,
  endDate,
) => {
  try {
    const allEntryIds = [];

    for (const plannable of plannables) {
      const query = {
        filter: {
          employee: { _eq: employeeId },
          project: { _eq: plannable.project.id },
          job: { _eq: plannable.job.id },
          milestone: { _eq: plannable.milestone?.id || null },
          date: { _between: [startDate, endDate] },
          planned_hours: { _nnull: true },
        },
      };

      const existingEntries = await getItemsFromCollection("timesheet", query);
      const entryIds = existingEntries.map((entry) => entry.id);
      allEntryIds.push(...entryIds);
    }

    if (allEntryIds.length > 0) {
      await deleteItemsFromCollection("timesheet", allEntryIds);
    }

    return { deletedCount: allEntryIds.length };
  } catch (error) {
    console.error("Error bulk deleting timesheet entries:", error);
    throw error;
  }
};

export const saveTimesheetBatch = async (userId, plannables) => {
  try {
    const entriesToCreate = [];

    for (const plannable of plannables) {
      for (const date in plannable.planned_hours) {
        const hours = plannable.planned_hours[date];
        const projectId = plannable.project?.id;
        const jobId = plannable.job?.id;
        const milestoneId = plannable.milestone?.id || null;

        if (projectId && jobId && hours > 0) {
          entriesToCreate.push({
            employee: userId,
            project: projectId,
            job: jobId,
            milestone: milestoneId,
            date: date,
            planned_hours: hours,
          });
        }
      }
    }

    if (entriesToCreate.length > 0) {
      await addItemsToCollection("timesheet", entriesToCreate);
    }

    return {
      created: entriesToCreate.length,
    };
  } catch (error) {
    console.error("Error saving timesheet batch:", error);
    throw error;
  }
};
